import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoLandingPage from "@/components/SeoLandingPage";
import DumpGuideLinks from "@/components/DumpGuideLinks";
import JobStories from "@/components/JobStories";
import { getCity, CITIES, SERVICES, SITE, PHONE_TEL, PROVIDER } from "@/seoData";

export default function CityPage() {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <><Helmet><meta name="robots" content="noindex" /></Helmet><Navigate to="/junk-removal/san-diego" replace /></>;

  const name = city.name;
  const canonical = `${SITE}/junk-removal/${slug}`;
  const title = city.titleOverride || `Junk Removal in ${name}, CA | Same-Day Hauling | CleanOut247`;
  const h1 = city.h1Override || `Junk Removal in ${name}, CA`;
  const description = city.descriptionOverride || `Need junk removal in ${name}? CleanOut247 offers same-day, upfront-priced junk hauling, furniture & appliance removal, and full cleanouts across ${name} and San Diego County. Licensed & insured. Call/Text (619) 999-3565.`;

  const faqs = [
    { q: `How much does junk removal cost in ${name}?`, a: `Pricing is based on how much space your items take up in our truck, starting around $130 for a small load up to about $900 for a full truck. You'll always get an upfront, no-obligation price in ${name} before we start — no hidden fees.` },
    { q: `Do you offer same-day junk removal in ${name}?`, a: `Yes. We offer same-day and next-day appointments throughout ${name} and all of San Diego County, 7 days a week. Call or text (619) 999-3565 and we'll get you scheduled fast.` },
    { q: `What items do you take in ${name}?`, a: `We haul furniture, mattresses, appliances, yard waste, construction debris, garage and estate cleanouts, hot tubs and more. We can't take hazardous materials (paint, chemicals, fuels), but we'll point you to the right drop-off.` },
    { q: `Are you licensed and insured?`, a: `Absolutely. CleanOut247 is fully licensed and insured, and we recycle or donate whatever we can to keep junk out of San Diego landfills.` },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Junk Removal",
      provider: PROVIDER,
      areaServed: city.parent ? { "@type": "Place", name: `${name}, San Diego, CA` } : { "@type": "City", name: `${name}, CA` },
      name: `Junk Removal in ${name}, CA`,
      description,
      url: canonical,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: `Junk Removal ${name}`, item: canonical },
      ],
    },
  ];

  const related = city.parent
    ? [
        { to: `/junk-removal/${city.parent}`, label: "All San Diego Neighborhoods" },
        ...CITIES.filter((c) => c.parent && c.slug !== slug).map((c) => ({ to: `/junk-removal/${c.slug}`, label: `Junk Removal ${c.name}` })),
        ...SERVICES.slice(0, 4).map((s) => ({ to: `/services/${s.slug}`, label: s.name })),
      ]
    : [
        ...(city.hub ? CITIES.filter((c) => c.slug !== slug) : CITIES.filter((c) => c.slug !== slug && !c.parent).slice(0, 6)).map((c) => ({ to: `/junk-removal/${c.slug}`, label: `Junk Removal ${c.name}` })),
        ...(city.hub ? [] : SERVICES.slice(0, 3).map((s) => ({ to: `/services/${s.slug}`, label: s.name }))),
      ];

  const config = {
    h1,
    crumb: city.hub ? "San Diego Neighborhoods" : `Serving ${name}`,
    lead: city.blurb,
    leadHtml: city.leadHtml,
    neighborhoods: city.neighborhoods,
    neighborhoodsTitle: `Neighborhoods we serve in ${name}`,
    benefitsTitle: `What we haul in ${name}`,
    sections: city.sections,
    seo: { title, description, canonical, jsonLd },
    extra: (
      <>
        <DumpGuideLinks place={name} />
        <JobStories cityName={name} cityNeighborhoods={city.neighborhoods || []} />
      </>
    ),
    faqs,
    related,
    relatedTitle: city.hub ? "Every San Diego area & city we serve" : city.parent ? "Other San Diego neighborhoods & services" : "Nearby areas & services we cover",
  };

  return <SeoLandingPage config={config} />;
}
